import React, { useState, useEffect, useRef } from "react";
import moment from "moment";
import {
  Typography,
  Button,
  Icon,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { coloresEmma, obtenerValores } from "../../../global/js/funciones";
import { BtnVer } from "../../general/BotonesAccion";
import TareasTerminadas from "../../general/TareasTerminadas";
import emma_s1 from "../../../global/imagenes/emma_s1.png";
import emma_w from "../../../global/imagenes/emma_w.png";
import { useStyles } from "../styles";
import ProyectoVidaDetalle from "../ProyectoVidaDetalle";
import { GENERICA_PROGRAMAS, SEMESTRES, PROYECTO_VACIO, COLORES_AVATAR } from "../helper";
import {
  buscarEstudiantesGestion,
  listarEstudiantesGestion,
  obtenerProyectoEstudiante,
  obtenerVersionesEstudiante,
  obtenerFotoEstudiante,
} from "../services";

const nombreCompleto = (e) =>
  [e.primer_nombre, e.segundo_nombre, e.primer_apellido, e.segundo_apellido].filter(Boolean).join(" ");

const iniciales = (e) => `${(e.primer_nombre || "")[0] || ""}${(e.primer_apellido || "")[0] || ""}`.toUpperCase();

export default function ConsultaSeguimiento({ actualizarMensaje }) {
  const classes = useStyles();

  // Filtros
  const [opcionesProgramas, setOpcionesProgramas] = useState([]);
  const [programa, setPrograma] = useState(null);
  const [semestre, setSemestre] = useState("");
  const [estudianteSel, setEstudianteSel] = useState(null);
  const [opcionesEstudiantes, setOpcionesEstudiantes] = useState([]);
  const [buscandoEstu, setBuscandoEstu] = useState(false);
  const [inputEstudiante, setInputEstudiante] = useState("");
  const timerRef = useRef(null);

  // Tabla
  const [resultados, setResultados] = useState([]);
  const [buscado, setBuscado] = useState(false);
  const [cargandoTabla, setCargandoTabla] = useState(false);
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;

  // Detalle
  const [vista, setVista] = useState("lista"); // 'lista' | 'detalle'
  const [estudianteDetalle, setEstudianteDetalle] = useState(null);
  const [detalleDatos, setDetalleDatos] = useState({ ...PROYECTO_VACIO });
  const [detalleVersiones, setDetalleVersiones] = useState([]);
  const [detalleVersionSel, setDetalleVersionSel] = useState("actual");
  const [detalleFoto, setDetalleFoto] = useState(null);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);

  // Reglas de activación de filtros (se deshabilitan, no se ocultan)
  const deshabilitarAB = !!estudianteSel;
  const deshabilitarC = !!programa;
  const buscarHabilitado = !!programa || !!estudianteSel;

  // Cargar programas (genérica 5)
  useEffect(() => {
    let activo = true;
    (async () => {
      const data = await obtenerValores(GENERICA_PROGRAMAS);
      if (activo && Array.isArray(data)) {
        setOpcionesProgramas(data.map((p) => ({ value: p.id, label: p.nombre })));
      }
    })();
    return () => {
      activo = false;
    };
  }, []);

  // Autocomplete de estudiantes (debounce -> personas/buscar solo estudiantes)
  const buscarEstudiantes = (value) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!value || value.trim().length < 4) {
      setOpcionesEstudiantes([]);
      return;
    }
    setBuscandoEstu(true);
    timerRef.current = setTimeout(async () => {
      const { estado, resp } = await buscarEstudiantesGestion(value.trim());
      let ops = [];
      if (estado === 200 && Array.isArray(resp)) {
        ops = resp.map((e) => ({ value: e.id, label: `${nombreCompleto(e)} - ${e.identificacion}`, estudiante: e }));
      }
      setOpcionesEstudiantes(ops);
      setBuscandoEstu(false);
    }, 700);
  };

  const buscar = async () => {
    setCargandoTabla(true);
    setBuscado(true);
    setPage(0);
    const filtros = {};
    if (estudianteSel) filtros.estudiante = estudianteSel.value;
    else if (programa) {
      filtros.programa = programa.value;
      if (semestre) filtros.semestre = semestre;
    }
    const { estado, resp } = await listarEstudiantesGestion(filtros);
    let lista = [];
    if (estado === 200 && resp && Array.isArray(resp.estudiantes)) lista = resp.estudiantes;
    else if (actualizarMensaje) {
      actualizarMensaje({ titulo: "No se pudo obtener el listado, intenta más tarde.", tipo: "error", mostrar: true, tiempo: 6000 });
    }
    setResultados(lista);
    setCargandoTabla(false);
  };

  const limpiar = () => {
    setPrograma(null);
    setSemestre("");
    setEstudianteSel(null);
    setOpcionesEstudiantes([]);
    setInputEstudiante("");
    setResultados([]);
    setBuscado(false);
    setPage(0);
  };

  const verDetalle = async (estudiante) => {
    setEstudianteDetalle(estudiante);
    setVista("detalle");
    setCargandoDetalle(true);
    setDetalleVersionSel("actual");
    setDetalleFoto(null);
    // Foto del estudiante (Graph): en paralelo, sin bloquear el detalle.
    obtenerFotoEstudiante(estudiante.id).then(({ estado, resp }) => {
      if (estado === 200 && resp && resp.foto) setDetalleFoto(resp.foto);
    });
    try {
      const [pRes, vRes] = await Promise.all([
        obtenerProyectoEstudiante(estudiante.id),
        obtenerVersionesEstudiante(estudiante.id),
      ]);
      let datos = { ...PROYECTO_VACIO };
      if (pRes.estado === 200 && pRes.resp && pRes.resp.existe && pRes.resp.proyecto) {
        const p = pRes.resp.proyecto;
        Object.keys(PROYECTO_VACIO).forEach((k) => {
          datos[k] = p[k] || "";
        });
      }
      setDetalleDatos(datos);
      setDetalleVersiones(vRes.estado === 200 && vRes.resp && Array.isArray(vRes.resp.versiones) ? vRes.resp.versiones : []);
    } catch (e) {
      setDetalleDatos({ ...PROYECTO_VACIO });
      setDetalleVersiones([]);
    }
    setCargandoDetalle(false);
  };

  // ---------------------------------------------------------------- Detalle
  if (vista === "detalle" && estudianteDetalle) {
    const e = estudianteDetalle;
    const versionesOpciones = detalleVersiones.length
      ? detalleVersiones.map((v) => ({
          value: v.id,
          label: v.actual
            ? "Versión actual"
            : v.fecha
            ? moment(v.fecha).format("DD/MM/YYYY hh:mm a")
            : "Versión anterior",
        }))
      : [{ value: "actual", label: "Versión actual" }];
    const versionActiva = detalleVersiones.find((v) => String(v.id) === String(detalleVersionSel));
    const datosDetalle = versionActiva ? versionActiva.datos : detalleDatos;

    return (
      <div style={{ padding: "12px 8px 40px 8px" }}>
        <Button
          onClick={() => setVista("lista")}
          startIcon={<Icon>arrow_back</Icon>}
          style={{ textTransform: "none", fontWeight: 700, color: coloresEmma.secondarycolor, marginBottom: 8 }}
        >
          Regresar
        </Button>
        {cargandoDetalle ? (
          <TareasTerminadas mensaje="Cargando.." marginTop="7%" imagen={emma_w} widthImg="7%" cargando={true} />
        ) : (
          <ProyectoVidaDetalle
            datos={datosDetalle}
            usuario={{
              primer_nombre: e.primer_nombre,
              primer_apellido: e.primer_apellido,
              segundo_apellido: e.segundo_apellido,
            }}
            programas={(e.programas || []).map((n) => ({ relacion: { nombre: n } }))}
            versiones={versionesOpciones}
            versionSeleccionada={detalleVersionSel}
            onCambiarVersion={(id) => setDetalleVersionSel(id)}
            mostrarEditar={false}
            fotoLocal={false}
            fotoBase64={detalleFoto}
          />
        )}
      </div>
    );
  }

  // ---------------------------------------------------------------- Lista
  const paginados = resultados.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <div style={{ padding: "20px 28px 60px 28px" }}>
      <Typography variant="h6" style={{ fontWeight: 700, color: "#2f2f2f" }}>
        Consulta de Proyecto de Vida
      </Typography>
      <Typography variant="body2" style={{ color: "#6b6b6b", marginBottom: 20 }}>
        Consulta y realiza seguimiento a la información registrada por los estudiantes.
      </Typography>

      {/* Filtros */}
      <div className={classes.filtrosCard}>
        <div className={classes.filtrosRow}>
          <FormControl className="form-control" style={{ flex: 1, minWidth: 220 }}>
            <Autocomplete
              options={opcionesProgramas}
              getOptionLabel={(option) => option.label}
              getOptionSelected={(option, value) => option.value === value.value}
              value={programa}
              disabled={deshabilitarAB}
              onChange={(event, newValue) => {
                setPrograma(newValue);
                if (!newValue) setSemestre("");
              }}
              renderInput={(params) => <TextField {...params} label="Programa académico" />}
              noOptionsText="Sin resultados"
            />
          </FormControl>

          <FormControl className="form-control" style={{ flex: 1, minWidth: 200 }} disabled={deshabilitarAB || !programa}>
            <InputLabel>Semestre</InputLabel>
            <Select
              value={semestre}
              onChange={(e) => setSemestre(e.target.value)}
              endAdornment={
                semestre !== "" && !deshabilitarAB && programa ? (
                  <IconButton
                    size="small"
                    style={{ marginRight: 18, padding: 2 }}
                    onClick={() => setSemestre("")}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <Icon fontSize="small">close</Icon>
                  </IconButton>
                ) : null
              }
            >
              {SEMESTRES.map((s) => (
                <MenuItem key={s} value={s}>
                  Semestre {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl className="form-control" style={{ flex: 1, minWidth: 260 }}>
            <Autocomplete
              options={opcionesEstudiantes}
              getOptionLabel={(option) => option.label}
              getOptionSelected={(option, value) => option.value === value.value}
              value={estudianteSel}
              disabled={deshabilitarC}
              onChange={(event, newValue) => setEstudianteSel(newValue)}
              onInputChange={(event, value, reason) => {
                if (reason === "input") {
                  setInputEstudiante(value);
                  buscarEstudiantes(value);
                } else if (reason === "clear") {
                  setInputEstudiante("");
                }
              }}
              filterOptions={(x) => x}
              loading={buscandoEstu}
              loadingText="Buscando..."
              noOptionsText={
                inputEstudiante.trim().length < 4
                  ? "Escribe al menos 4 caracteres"
                  : "No se encontraron estudiantes"
              }
              renderInput={(params) => (
                <TextField {...params} label="Número de documento, nombre o usuario" />
              )}
            />
          </FormControl>

          <div className={classes.filtrosAcciones}>
            <Button
              variant="outlined"
              onClick={limpiar}
              startIcon={<Icon>filter_alt</Icon>}
              className={classes.btnCancelar}
            >
              Limpiar filtros
            </Button>
            <Button
              variant="contained"
              onClick={buscar}
              disabled={!buscarHabilitado}
              startIcon={<Icon>search</Icon>}
              className={classes.btnPrimario}
            >
              Buscar
            </Button>
          </div>
        </div>
      </div>

      {/* Resultados */}
      {cargandoTabla ? (
        <TareasTerminadas mensaje="Cargando.." marginTop="7%" imagen={emma_w} widthImg="7%" cargando={true} />
      ) : buscado && resultados.length > 0 ? (
        <div style={{ marginTop: 24 }}>
          <Typography variant="subtitle1" style={{ fontWeight: 700, color: "#2f2f2f", marginBottom: 8 }}>
            Estudiantes con proyecto de vida completado
          </Typography>

          <div style={{ overflowX: "auto", border: "1px solid #eee", borderRadius: 8 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.thGestion}>Estudiante</TableCell>
                  <TableCell className={classes.thGestion}>Documento</TableCell>
                  <TableCell className={classes.thGestion}>Programa</TableCell>
                  <TableCell className={classes.thGestion}>Semestre</TableCell>
                  <TableCell className={classes.thGestion}>Actualización</TableCell>
                  <TableCell className={classes.thGestion} align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginados.map((e) => (
                  <TableRow key={e.id} hover>
                    <TableCell>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div
                          className={classes.avatarIniciales}
                          style={{ backgroundColor: COLORES_AVATAR[e.id % COLORES_AVATAR.length] }}
                        >
                          {iniciales(e)}
                        </div>
                        <span>{nombreCompleto(e)}</span>
                      </div>
                    </TableCell>
                    <TableCell>{e.documento}</TableCell>
                    <TableCell>{(e.programas || []).join(", ")}</TableCell>
                    <TableCell>
                      {e.semestre != null ? (
                        e.semestre
                      ) : (
                        <span className={classes.badgeSemestre}>{e.semestre_texto || "No presenta"}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {e.fecha_actualiza ? moment(e.fecha_actualiza).format("DD/MM/YYYY") : "—"}
                    </TableCell>
                    <TableCell align="center">
                      <BtnVer texto="Ver información" callback={() => verDetalle(e)} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={resultados.length}
              page={page}
              onPageChange={(e, nueva) => setPage(nueva)}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[]}
              labelDisplayedRows={({ from, to, count }) => `Mostrando ${from} a ${to} de ${count} estudiantes`}
            />
          </div>
        </div>
      ) : (
        <TareasTerminadas
          mensaje={
            buscado
              ? "No se encontraron estudiantes con los filtros seleccionados o aún no han completado su proyecto de vida"
              : "Selecciona un programa o busca un estudiante para consultar la información"
          }
          marginTop="7%"
          imagen={emma_s1}
          widthImg="7%"
        />
      )}
    </div>
  );
}
